import { inject, Injectable, resource } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';

import { Auth, onAuthStateChanged, onIdTokenChanged, User } from 'firebase/auth';
import { limit, query, Timestamp, where } from 'firebase/firestore';
import { Observable } from 'rxjs';

import { ClientsDao } from '../../data/daos/clients.dao';
import { UsersDao } from '../../data/daos/users.dao';
import { UserClaims } from '../../data/models/user.model';
import { FirebaseService } from '../../shared/services/firebase.service';
import { LocalStorageUtil } from '../../shared/utils/local-storage.util';
import { AuthStateService } from './auth-state.service';

@Injectable({ providedIn: 'root' })
export class AuthService {
	private readonly clientsDao = inject(ClientsDao);
	private readonly firebaseService = inject(FirebaseService);
	private readonly auth = this.firebaseService.auth;

	private readonly authUser = toSignal(user(this.auth));
	readonly idToken = resource({
		params: () => ({
			user: this.authUser(),
		}),
		loader: ({ params: { user } }) => user.getIdToken(),
	});

	constructor(
		private usersDao: UsersDao,
		private authStateService: AuthStateService,
	) {}

	init() {
		authState(this.auth).subscribe((user) => {
			if (!user) {
				this.authStateService.loggedOut.next();
				if (!/^(\/login$)|(\/auth\/)/.test(window.location.pathname)) {
					window.location.href = '/auth/login';
				}
				LocalStorageUtil.clear();
			} else {
				void this.setLocalStorageForUser(user);
				void this.updateAuthDetails(user);
			}
		});
	}

	async setLocalStorageForUser(user: User) {
		const { claims } = await user.getIdTokenResult();
		const { isInternal, clientIds, role } = claims as unknown as UserClaims;
		LocalStorageUtil.setIsInternal(isInternal);
		LocalStorageUtil.setRole(role);
		LocalStorageUtil.setUserId(user.uid);
		LocalStorageUtil.setClientIds(clientIds);

		if (LocalStorageUtil.getActiveClientId() == null) {
			let activeClientId: string | undefined;
			if (!isInternal) {
				activeClientId = clientIds[0];
			} else {
				const [activeClient] = await this.clientsDao
					.collection()
					.getAll((ref) => query(ref, where('tools', '!=', []), limit(1)));
				activeClientId = activeClient?.id;
			}
			if (activeClientId) {
				LocalStorageUtil.setActiveClientId(activeClientId);
			}
		}
	}

	async updateAuthDetails(user: User) {
		const isEmailVerified = user.emailVerified;
		await this.usersDao.update(user.uid, {
			lastAccessed: Timestamp.fromDate(new Date()),
			isEmailVerified,
		});
	}
}

// Copied from rxfire: https://github.com/FirebaseExtended/rxfire/blob/main/auth/index.ts
export function authState(auth: Auth): Observable<User | null> {
	return new Observable((subscriber) => {
		const unsubscribe = onAuthStateChanged(
			auth,
			/* eslint-disable @typescript-eslint/no-unsafe-argument */
			subscriber.next.bind(subscriber),
			subscriber.error.bind(subscriber),
			subscriber.complete.bind(subscriber),
			/* eslint-enable @typescript-eslint/no-unsafe-argument */
		);
		return { unsubscribe };
	});
}

// Copied from rxfire: https://github.com/FirebaseExtended/rxfire/blob/main/auth/index.ts
export function user(auth: Auth): Observable<User | null> {
	return new Observable((subscriber) => {
		const unsubscribe = onIdTokenChanged(
			auth,
			/* eslint-disable @typescript-eslint/no-unsafe-argument */
			subscriber.next.bind(subscriber),
			subscriber.error.bind(subscriber),
			subscriber.complete.bind(subscriber),
			/* eslint-enable @typescript-eslint/no-unsafe-argument */
		);
		return { unsubscribe };
	});
}
